import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from './pages/NewLandingPage';
import FreeTestPage from './pages/FreeTestPage';
import UpgradePage from './pages/UpgradePage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import About from './pages/About';
import Resources from './pages/Resources';
import Blog from './pages/Blog';
import News from './pages/News';
import Login from './pages/Login';
import Register from './pages/Register';
import StudentDashboard from './pages/StudentDashboard';
import ParentDashboard from './pages/ParentDashboard';
import TeacherDashboard from './pages/TeacherDashboardNew';
import InstitutionDashboard from './pages/InstitutionDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboardNew';
import EmployeeDashboard from './pages/EmployeeDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import HRDashboard from './pages/HRDashboard';
import AssessmentIntro from './pages/AssessmentIntro';
import AssessmentQuestions from './pages/AssessmentQuestions';
import ObserverAssessment from './pages/ObserverAssessment';
import ReportView from './pages/ReportView';
import Pricing from './pages/Pricing';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import CoachPage from './pages/CoachPage';
import KVKK from './pages/legal/KVKK';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Cookies from './pages/legal/Cookies';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: '/api',
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!user) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  const getDashboardByRole = () => {
    if (!user) return <Navigate to="/" replace />;
    switch (user.role) {
      case 'student':
        return <StudentDashboard user={user} logout={logout} setUser={setUser} />;
      case 'parent':
        return <ParentDashboard user={user} logout={logout} setUser={setUser} />;
      case 'teacher':
        return <TeacherDashboard user={user} logout={logout} setUser={setUser} />;
      case 'admin':
      case 'institution_admin':
      case 'mudur':
      case 'mudur_yardimcisi':
      case 'rehber_ogretmen':
      case 'psikolog':
        return <InstitutionDashboard user={user} logout={logout} setUser={setUser} />;
      case 'super_admin':
        return <SuperAdminDashboard user={user} logout={logout} setUser={setUser} />;
      case 'employee':
        return <EmployeeDashboard user={user} logout={logout} setUser={setUser} />;
      case 'manager':
        return <ManagerDashboard user={user} logout={logout} setUser={setUser} />;
      case 'hr':
        return <HRDashboard user={user} logout={logout} setUser={setUser} />;
      case 'individual':
        return <StudentDashboard user={user} logout={logout} setUser={setUser} isIndividual={true} />;
      default:
        return <Navigate to="/" replace />;
    }
  };

  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? getDashboardByRole() : <LandingPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/news" element={<News />} />
          <Route path="/kvkk" element={<KVKK />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login setUser={setUser} />} />
          <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register setUser={setUser} />} />
          <Route path="/free-test" element={user ? <Navigate to="/assessment" replace /> : <FreeTestPage setUser={setUser} />} />
          <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />
          <Route path="/reset-password" element={user ? <Navigate to="/" replace /> : <ResetPassword />} />
          
          <Route
            path="/assessment/intro"
            element={
              <ProtectedRoute allowedRoles={['student', 'parent', 'individual']}>
                <AssessmentIntro user={user} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessment/:assessmentId"
            element={
              <ProtectedRoute allowedRoles={['student', 'parent', 'individual', 'employee', 'teacher', 'rehber_ogretmen', 'psikolog', 'mudur', 'mudur_yardimcisi', 'institution_admin', 'admin', 'super_admin']}>
                <AssessmentQuestions user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/observer-assessment/:assessmentId"
            element={
              <ProtectedRoute allowedRoles={['teacher', 'rehber_ogretmen', 'psikolog', 'mudur', 'mudur_yardimcisi', 'institution_admin', 'admin', 'super_admin', 'parent']}>
                <ObserverAssessment user={user} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/assessment"
            element={
              <ProtectedRoute allowedRoles={['student', 'parent', 'individual', 'employee']}>
                <AssessmentIntro user={user} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/report/:reportId"
            element={
              <ProtectedRoute>
                <ReportView user={user} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/pricing"
            element={<Pricing />}
          />
          
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/subscription/cancel"
            element={
              <ProtectedRoute>
                <SubscriptionCancel />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/coach"
            element={
              <ProtectedRoute>
                <CoachPage user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/upgrade"
            element={<UpgradePage user={user} />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;